/**
 * @file site-access-store.ts
 * @description Кэш ответа `GET admin/site-access` — открыт ли сайт для
 * покупателей (phase19.16, SB138; db-design-site-closed.md §6). Одна запись
 * на вкладку кабинета, помеченная кабинетом (`key` — контекст магазина):
 * запись чужого кабинета не показывается. Образец — `live-issues-store.ts`.
 *
 * Кроме данных запись хранит поправку часов (`offsetMs`): время сервера
 * минус время браузера в момент ответа. Плашка и окно считают «N мин»,
 * «откроется сам в 15:20» и «≤ 10 мин до срока» от часов сервера —
 * неверные часы компьютера сотрудника не сдвигают напоминание.
 *
 * Порядок записей: ответ своего действия (`applySiteAccess`) увеличивает
 * номер записи `seq`; ответ опроса, начатого до последнего действия, — уже
 * устаревший и отбрасывается (иначе «закрыли сайт → прилетел старый GET →
 * плашка пропала до следующего опроса»).
 *
 * Сброс (`invalidateSiteAccess`) — при смене кабинета вкладки
 * (`selectTabContext` в AuthProvider). Ошибка запроса не стирает последнее
 * известное состояние того же кабинета: плашка не мигает от сбоя сети.
 * Без зависимостей от API-клиента; подписка — для `useSyncExternalStore`.
 */

import type { SiteAccessStateDto } from "@chaiberi/shared";

/** Запись кэша: чей кабинет, данные, ошибка последнего запроса, поправка часов. */
export interface SiteAccessEntry {
  key: string;
  /** Последнее известное состояние; `null` — ни один запрос не удался. */
  data: SiteAccessStateDto | null;
  /** Последний запрос не удался. */
  error: boolean;
  /** Время сервера минус время браузера, мс. */
  offsetMs: number;
  /** Когда записано (часы браузера). */
  at: number;
}

let entry: SiteAccessEntry | null = null;
let generation = 0;
/** Номер последнего своего действия: ответы запросов, начатых раньше, не записываются. */
let seq = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/** Поправка часов по `serverTime`; битое время сервера — без поправки. */
export function clockOffsetMs(serverTime: string, clientNow: number): number {
  const server = Date.parse(serverTime);
  return Number.isFinite(server) ? server - clientNow : 0;
}

/** Текущая запись (`null` — не загружено или сброшено). */
export function siteAccessEntry(): SiteAccessEntry | null {
  return entry;
}

/** Номер сброса: ответ запроса, начатого до сброса, не записывается. */
export function siteAccessGeneration(): number {
  return generation;
}

/** Номер последнего своего действия — запоминается при старте запроса. */
export function siteAccessSeq(): number {
  return seq;
}

/** Ответ устарел: сброс или своё действие после старта запроса. */
function stale(startedAt: number, startedSeq: number): boolean {
  return startedAt !== generation || startedSeq !== seq;
}

/** Записать ответ запроса кабинета `key`, начатого при поколении `startedAt` и номере `startedSeq`. */
export function storeSiteAccess(startedAt: number, key: string, data: SiteAccessStateDto, now = Date.now(), startedSeq = seq): void {
  if (stale(startedAt, startedSeq)) return;
  entry = { key, data, error: false, offsetMs: clockOffsetMs(data.serverTime, now), at: now };
  emit();
}

/** Записать ошибку: прежние данные того же кабинета остаются. */
export function storeSiteAccessError(startedAt: number, key: string, now = Date.now(), startedSeq = seq): void {
  if (stale(startedAt, startedSeq)) return;
  const same = entry !== null && entry.key === key ? entry : null;
  entry = { key, data: same?.data ?? null, error: true, offsetMs: same?.offsetMs ?? 0, at: now };
  emit();
}

/** Ответ своего действия (close/open/extend): новее любого запроса в полёте. */
export function applySiteAccess(key: string, data: SiteAccessStateDto, now = Date.now()): void {
  seq += 1;
  entry = { key, data, error: false, offsetMs: clockOffsetMs(data.serverTime, now), at: now };
  emit();
}

/** Время сервера по записи: часы браузера с поправкой. */
export function siteAccessNow(record: SiteAccessEntry | null, clientNow = Date.now()): Date {
  return new Date(clientNow + (record?.offsetMs ?? 0));
}

/** Сбросить кэш (смена кабинета вкладки): следующий показ перечитает сервер. */
export function invalidateSiteAccess(): void {
  entry = null;
  generation += 1;
  emit();
}

/** Подписка на смену кэша; возвращает отписку. */
export function subscribeSiteAccess(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
